/**
 * Elegir entre pocas opciones, sin abrir nada.
 *
 * Un desplegable de dos opciones son tres toques —abrir, elegir, cerrar— para
 * decidir algo que entra entero en la pantalla. Con chips es uno, y además se
 * ve qué está elegido sin tener que abrir nada.
 *
 * El corte está en cuántas opciones hay y en si son fijas:
 *
 *   dos o tres opciones fijas  → chips
 *   listas largas o que crecen → buscador o desplegable
 *
 * Clientes, establecimientos, honorarios y participantes siguen siendo listas:
 * crecen con el uso y convertirlas en chips llenaría la pantalla.
 */
export function Chip({
  activo,
  onClick,
  disabled = false,
  testId,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  disabled?: boolean;
  testId?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={activo}
      // El criterio unico de seleccion de toda la aplicacion.
      //
      // Antes el activo cambiaba `border-slate-900` y una negrita, sin fondo.
      // En tema claro era sutil; en tema OSCURO `border-slate-900` es mas
      // oscuro que el fondo, asi que el borde del seleccionado directamente
      // desaparecia: quedaba peor marcado que el que no lo estaba. En el
      // reparto economico eso se paga caro, porque elegir mal no avisa.
      //
      // Ahora el seleccionado invierte fondo y texto. Se distingue de un
      // vistazo, sin tener que comparar contra el de al lado.
      className={`inline-flex min-h-11 items-center gap-1.5 rounded-full border border-[var(--linea)] px-3 text-sm disabled:opacity-50 ${
        activo
          ? 'border-[var(--accion)] bg-[var(--accion)] font-semibold text-[var(--sobre-accion)]'
          : 'border-[var(--linea-fuerte)] bg-transparent'
      }`}
      data-testid={testId}
    >
      {/* El tilde no es decoracion: es la mitad no cromatica de la senal, para
          quien no distingue el cambio de fondo. */}
      {activo && <span aria-hidden>✓</span>}
      {children}
    </button>
  );
}

/** Un grupo de chips que se comporta como un `select` de pocas opciones. */
export function Chips<T extends string>({
  value,
  onChange,
  options,
  disabled = false,
  testId,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((o) => (
        <Chip
          key={o.value}
          activo={value === o.value}
          onClick={() => onChange(o.value)}
          disabled={disabled}
          testId={testId ? `${testId}-${o.value}` : undefined}
        >
          {o.label}
        </Chip>
      ))}
    </div>
  );
}
